import * as Sentry from "@sentry/react";

import { noteCrashDetectorBreadcrumb } from "utils/crashDetector";
import { getSentryRelease } from "utils/sentryRelease";

import { enhanceSentryErrorContext, shouldFilterSentryError } from "./sentryErrorFilter";

export { getSentryRelease };

// Initialize Sentry as early as possible in your application's lifecycle
export function initSentry() {
  // Skip Sentry initialization entirely when running on localhost
  // unless explicitly enabled via environment variable
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "0.0.0.0";

  if (isLocalhost && process.env.REACT_APP_SENTRY_DEBUG !== "true") {
    // eslint-disable-next-line no-console
    console.info(
      "Sentry disabled for localhost. Set REACT_APP_SENTRY_DEBUG=true to enable."
    );
    return;
  }

  // Read Sentry configuration from environment variables
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const sentryEnvironment = process.env.REACT_APP_SENTRY_ENVIRONMENT;
  const tracesSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0"
  );
  const replaysSessionSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || "0"
  );
  const enableTracing = process.env.REACT_APP_SENTRY_ENABLE_TRACING === "true";

  // Require DSN to be set
  if (!sentryDsn) {
    // eslint-disable-next-line no-console
    console.warn(
      "Sentry DSN not found (REACT_APP_SENTRY_DSN). Sentry will not be initialized."
    );
    return;
  }

  // Determine current environment
  const reactAppEnv = process.env.REACT_APP_ENV || "development";

  const release = getSentryRelease();

  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment || reactAppEnv, // Use reactAppEnv as fallback
    release: release,
    integrations: [
      Sentry.browserTracingIntegration({
        // Tracing enabled based on environment configuration
        enableInp: enableTracing
      }),
      Sentry.replayIntegration()
    ],
    // Tracing configuration - Uses environment-specific rates
    tracesSampleRate: enableTracing ? tracesSampleRate : 0,
    // CRITICAL: Only propagate traces to our own origin
    // This prevents Sentry from adding trace headers to ANY external domain
    // (e.g. Salesforce Web-to-Case) which causes CORS errors
    // Using regex with start anchor ensures ONLY same-origin requests get headers
    tracePropagationTargets: enableTracing
      ? [new RegExp(`^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`)]
      : [],

    // Session Replay configuration - Uses environment-specific rates
    replaysSessionSampleRate: replaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0, // Always capture replays on errors

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Mirror every breadcrumb into the crash detector's sessionStorage record —
    // Sentry's in-memory trail dies with the renderer on an "Aw Snap", the
    // persisted copy is attached to the crash report on the next load
    beforeBreadcrumb: (breadcrumb) => {
      noteCrashDetectorBreadcrumb(breadcrumb);
      return breadcrumb;
    },

    // Add environment-specific tags and context
    beforeSend: (event) => {
      // Filter out common non-error scenarios using the dedicated utility
      // (includes third-party services like Salesforce Web-to-Case)
      if (shouldFilterSentryError(event)) {
        return null; // Don't send to Sentry
      }

      // Enhance error context using the dedicated utility
      const enhancedEvent = enhanceSentryErrorContext(event, reactAppEnv);

      // Release is already set globally in Sentry.init

      return enhancedEvent;
    }
  });
}

// Export logger for use throughout the application
export const { logger } = Sentry;

// Error boundary component for React
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Function to manually capture exceptions
export const captureException = Sentry.captureException;

// Function to capture messages
export const captureMessage = Sentry.captureMessage;

// Function to start a custom span for performance monitoring
export const startSpan = Sentry.startSpan;

// Function to set user context
export const setUser = Sentry.setUser;

// Function to set tags
export const setTag = Sentry.setTag;

// Function to set additional context
export const setContext = Sentry.setContext;

// Function to add breadcrumbs
export const addBreadcrumb = Sentry.addBreadcrumb;
